// Angular Imports
import { Injectable, signal } from "@angular/core";
import { HttpParams } from "@angular/common/http";
import { FormGroup } from "@angular/forms";

// Rxjs
import { catchError, Observable, tap } from "rxjs";

// Ant Imports
import { NzUploadFile } from "ng-zorro-antd/upload";

// Models
import { CustomHttpErrorResponse } from "#models/custom-http-error-response";
import {
    FileData,
    FileManager,
    FileManagerPathPayload,
    FileManagerPersistedState,
    UploadSuccessResponse,
} from "#models/file-manager";

// Services
import { HttpService } from "#services/http/http.service";

// Custom Imports
import { TranslatePipe } from "#pipes/i18n/translate.pipe";
import { environment } from "#src/environments/environment";

@Injectable({
    providedIn: "root",
})
export class FileManagerService extends HttpService {
    private baseApiUrl = `${environment.API_URL}/filemanager`;
    private defaultStorageKey = "file_manager_state";

    isOpen = signal(false);
    translate = new TranslatePipe();
    defaultState = {
        path: "/",
        page: 1,
    };

    // Get 1 file
    getFile(
        props: {
            path: string;
            type: string;
        },
        onError?: (err: CustomHttpErrorResponse) => void
    ): Observable<{ data: { data: FileData } }> {
        const rawParams: Record<string, string | undefined> = {
            type: props.type,
            path: props.path,
        };

        const filteredParams = Object.fromEntries(
            Object.entries(rawParams).filter(
                (entry): entry is [string, string] => entry[1] !== undefined && entry[1] !== ""
            )
        );

        const params = new HttpParams({ fromObject: filteredParams });

        return this.http
            .get<{ data: { data: FileData } }>(`${this.baseApiUrl}/file`, { params })
            .pipe(
                catchError((error) => {
                    onError?.(error);

                    return this.handleRequestError(error);
                })
            );
    }

    // Get all folders and files
    getFoldersAndFiles(
        props: {
            pageIndex?: number;
            pageSize?: number;
            path?: string;
            type?: string;
            search?: string;
        },
        onError?: (err: CustomHttpErrorResponse) => void
    ): Observable<{ data: FileManager }> {
        const rawParams: Record<string, string | undefined> = {
            type: props.type,
            path: props.path,
            page: props.pageIndex?.toString(),
            per_page: props.pageSize?.toString(),
            search: props.search?.toString(),
        };

        const filteredParams = Object.fromEntries(
            Object.entries(rawParams).filter(
                (entry): entry is [string, string] => entry[1] !== undefined && entry[1] !== ""
            )
        );

        const params = new HttpParams({ fromObject: filteredParams });
        return this.http.get<{ data: FileManager }>(this.baseApiUrl, { params }).pipe(
            catchError((error) => {
                onError?.(error);

                return this.handleRequestError(error);
            })
        );
    }

    uploadFile(
        path: string,
        file: NzUploadFile,
        type: string,
        endpoint?: string
    ): Observable<UploadSuccessResponse> {
        const formData = new FormData();
        formData.append("type", type);
        formData.append("path", path);
        formData.append("file", file as unknown as File); // Explicit type casting to File

        return this.http
            .post<UploadSuccessResponse>(
                `${this.baseApiUrl}/file${endpoint ? `/${endpoint}` : ""}`,
                formData
            )
            .pipe(
                catchError((error) => this.handleRequestError(error)),
                tap(() =>
                    this.messageService.success(
                        this.translate.transform("i18n.file_manager.file_upload_success")
                    )
                )
            );
    }

    createFolder(props: {
        currentPath: string;
        formGroup: FormGroup;
        onError?: (err: CustomHttpErrorResponse) => void;
        type?: string;
    }) {
        const { formGroup, type, onError, currentPath } = props;
        const data = formGroup.value as FileManagerPathPayload;
        data.path = (currentPath === "/" ? currentPath : `/${currentPath}/`) + data.path;
        data.type = type || "default";

        return this.http.post<{ message: string }>(this.baseApiUrl + "/directory", data).pipe(
            catchError((error) => {
                onError?.(error);
                return this.handleRequestValidationError(error, formGroup);
            })
        );
    }

    getPersistedState(storageKey?: string): FileManagerPersistedState {
        if (typeof window === "undefined") {
            return this.defaultState;
        }

        const key = this.buildStorageKey(storageKey);
        const rawState = window.localStorage.getItem(key);

        if (!rawState) {
            return this.defaultState;
        }

        try {
            const parsed = JSON.parse(rawState) as Partial<FileManagerPersistedState>;

            return {
                path: this.normalizePath(parsed.path),
                page: this.normalizePage(parsed.page),
            };
        } catch {
            return this.defaultState;
        }
    }

    setPersistedState(state: Partial<FileManagerPersistedState>, storageKey?: string): void {
        if (typeof window === "undefined") {
            return;
        }

        const key = this.buildStorageKey(storageKey);
        const currentState = this.getPersistedState(storageKey);

        const nextState: FileManagerPersistedState = {
            path: this.normalizePath(state.path ?? currentState.path),
            page: this.normalizePage(state.page ?? currentState.page),
        };

        window.localStorage.setItem(key, JSON.stringify(nextState));
    }

    show() {
        this.isOpen.set(true);
    }

    hide() {
        this.isOpen.set(false);
    }

    private buildStorageKey(storageKey?: string): string {
        const normalizedStorageKey = storageKey?.trim();

        if (normalizedStorageKey) {
            return `${this.defaultStorageKey}:${normalizedStorageKey}`;
        }

        return this.defaultStorageKey;
    }

    private normalizePath(path?: string | null): string {
        return path?.trim() ? path : "/";
    }

    private normalizePage(page?: number | null): number {
        return typeof page === "number" && Number.isInteger(page) && page > 0 ? page : 1;
    }
}
